import { put, select } from 'redux-saga/effects';

import { tracker } from '../../utils/core';
import { achievementsSelectors } from '../achievements';
import { resourceSagaUtils, resourceStoreTypes } from '../core';
import type { BaseResource, QcsSyncResourcesConfigProps } from '../core/resourceStoreTypes';
import { featureFlagsSelectors } from '../feature-flags';
import * as actions from './milestonesActions';
import { isAchievementCompletedOrStarted } from './milestonesSelectors';
import { type MilestoneProps, mkMilestone } from './milestonesTypes';

type MilestonesSagaIterator = Generator<unknown, void, unknown>;
type MilestoneApi = Partial<MilestoneProps> & BaseResource;
type AchievementCompletionStatusLike = { isCompleted?: unknown; justStarted?: unknown };

const isObject = (value: unknown): value is object => typeof value === 'object' && value !== null;

const getAchievementName = (achievement: unknown): unknown => {
  if (isObject(achievement) && 'name' in achievement) {
    return achievement.name;
  }
  return undefined;
};

const getAchievementCompletionStatus = (value: unknown): AchievementCompletionStatusLike | undefined =>
  isObject(value) ? value : undefined;

const mkQcsSyncMilestonesConfig = resourceStoreTypes.createTypedQcsSyncResourcesConfigFactory<MilestoneProps>();

const mkResourceConfigMilestones = (props: Partial<QcsSyncResourcesConfigProps<MilestoneProps>>) =>
  mkQcsSyncMilestonesConfig({
    resourceName: 'milestones',
    resourceBaseUrl: '/gamification/milestones',
    // getLastSyncDate, // TODO: QCS need to fix it
    transformResponseToResources: (response) => response?.data?.resources?.map((data) => mkMilestone(data)),
    ...props,
  });

const { callFirst, callLast } = resourceSagaUtils.createGreedDebounce('milestonesDumpUpdater');
export function* milestonesDumpUpdater(resources: MilestoneApi[]): MilestonesSagaIterator {
  const bumpBadges = Boolean(yield select(featureFlagsSelectors.getFeatureFlag, 'bumpBadges'));
  if (bumpBadges) {
    yield callFirst(milestonesDumpUpdaterFull, resources);
    yield callLast(milestonesNudgeUpdater);
  }
}

function* milestonesDumpUpdaterFull(resources: MilestoneApi[]): MilestonesSagaIterator {
  try {
    const milestoneData = resources.map((data) => mkMilestone(data));
    yield put(actions.updateMilestone(milestoneData, { context: 'BUMP_CHANGE_DUMP' }));
    const achievementId = milestoneData[0]?.achievementId;
    const achievement = yield select(achievementsSelectors.getAchievementById, {
      // @ts-expect-error Preserve legacy JS behavior: missing achievementId should still reach the selector and fail inside this try/catch.
      id: achievementId,
    });
    const achievementName = getAchievementName(achievement);
    const completedObj = getAchievementCompletionStatus(
      yield select(isAchievementCompletedOrStarted, {
        achievementId,
      }),
    );
    tracker.track(tracker.events.milestonesEarned, {
      achievementName,
      count: milestoneData[0]?.currentCount,
      milestoneName: milestoneData[0]?.name,
    });
    if (completedObj?.isCompleted) {
      tracker.track(tracker.events.achievementComplete, { achievementName });
    }
    if (completedObj?.justStarted) {
      tracker.track(tracker.events.achievementStart, { achievementName });
    }
    yield put(actions.milestoneReceivedNotification({ milestoneData }));
  } catch (error) {
    assert(false, error);
  }
}

export function* milestonesNudgeUpdater(): MilestonesSagaIterator {
  const bumpBadges = Boolean(yield select(featureFlagsSelectors.getFeatureFlag, 'bumpBadges'));
  if (bumpBadges) {
    yield put(actions.getMilestones(undefined, { context: 'BUMP_CHANGE_NUDGE' }));
  }
}

export function* getMilestonesActionWatcher(): MilestonesSagaIterator {
  const config = mkResourceConfigMilestones({
    successAction: actions.getMilestonesSuccess,
    failureAction: actions.getMilestonesFailure,
  });
  yield resourceSagaUtils.takeAndDebounce(
    actions.getMilestones,
    resourceSagaUtils.qcsSyncGetResources<MilestoneProps>,
    config,
  );
}

export default [getMilestonesActionWatcher];
